/**
 * Mini Isometric Map Component
 * 
 * Compact isometric map showing center hex + 6 neighbors
 * Used in the action screen as an embedded mini map
 */

import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import { TerrainMap, AxialCoord, TerrainType } from '../types/terrain';
import { Player } from '../types/player';
import { hexNeighbors, pixelToHexIsometric, hexDepthIsometric, hexToPixelIsometric, hexCornersIsometric } from '../utils/hexMath';
import { drawHexIsometricSync, drawHighlightedHexIsometric, clearCanvas, fillBackground, drawRouteOverlay, drawRouteLine } from './HexRenderer';
import { FEATURES } from '../config/features';
import { drawSegmentedPlayerMarker, PlayerMarker as PlayerMarkerType, PLAYER_COLORS } from '../utils/playerMarkerRenderer';
import { getMarkerImage } from '../utils/playerMarkers';

interface MiniIsometricMapProps {
  map: TerrainMap;
  center: AxialCoord;
  playerPosition: AxialCoord;
  exploredHexes: Set<string>;
  onHexClick: (coord: AxialCoord) => void;
  onViewMapClick: () => void;
  player?: Player; // Player object for accessing selectedMarkerIcon
  playerStamina?: number;
  currentTerrain?: TerrainType;
  onForageClick?: () => void;
  canForage?: boolean;
  canMove?: boolean; // Whether player can currently move (not traveling/foraging)
  isInCombat?: boolean; // Whether player is in combat (disables map button)
  onRestHealClick?: () => void;
  pendingDestination?: AxialCoord | null;
  onAnimationComplete?: () => void;
  plannedRoute?: AxialCoord[];
  isStoryReady?: boolean;
}

const MiniIsometricMapComponent: React.FC<MiniIsometricMapProps> = ({
  map,
  center,
  playerPosition,
  exploredHexes,
  onHexClick,
  onViewMapClick,
  player,
  playerStamina = 0,
  currentTerrain,
  onForageClick,
  canForage = false,
  canMove = true,
  isInCombat = false,
  onRestHealClick,
  pendingDestination,
  onAnimationComplete,
  isStoryReady = false,
  plannedRoute,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Visual position for animation (starts at current position, animates to destination)
  const [visualPosition, setVisualPosition] = useState<AxialCoord | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const animationStartTimeRef = useRef<number | null>(null);
  const animationDuration = 1000; // 1 second animation
  const playerImageRef = useRef<HTMLImageElement | null>(null);
  const [hoveredHex, setHoveredHex] = useState<AxialCoord | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 600 });
  const [playerImageLoaded, setPlayerImageLoaded] = useState(false);
  const hasRenderedRef = useRef(false);
  
  // Touch/drag state for mobile panning
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastPanOffsetRef = useRef({ x: 0, y: 0 });
  
  // Fixed hex size optimized for mini map (19 hexes total)
  const HEX_SIZE = 70; // Balanced size for 2-ring display
  
  // Load player marker image based on selected marker
  useEffect(() => {
    const img = new Image();
    const markerImage = getMarkerImage(player?.selectedMarkerIcon);
    img.src = markerImage;
    img.onload = () => {
      playerImageRef.current = img;
      setPlayerImageLoaded(true);
    };
  }, [player?.selectedMarkerIcon]);
  
  // Initialize and sync visual position to current position (when not animating)
  useEffect(() => {
    if (!isAnimating) {
      // When not animating, always sync to player position
      // This ensures the map renders correctly after animation completes
      // We don't wait for pendingDestination to clear due to setTimeout timing issues
      if (visualPosition === null || 
          (visualPosition.q !== playerPosition.q || visualPosition.r !== playerPosition.r)) {
        setVisualPosition(playerPosition);
      }
    }
  }, [playerPosition, isAnimating, visualPosition]);
  
  // Handle animation cancellation (monster encounter mid-route)
  useEffect(() => {
    // If animation is running but pendingDestination was cleared (RESET), stop animation immediately
    if (isAnimating && !pendingDestination) {
      setIsAnimating(false);
      animationStartTimeRef.current = null;
      setVisualPosition(playerPosition);
    }
  }, [isAnimating, pendingDestination, playerPosition]);
  
  // Trigger animation when story is ready and destination is pending
  useEffect(() => {
    if (isStoryReady && pendingDestination && !isAnimating && visualPosition) {
      // Check if we're actually moving (visual position != destination)
      if (visualPosition.q !== pendingDestination.q || visualPosition.r !== pendingDestination.r) {
        setIsAnimating(true);
        animationStartTimeRef.current = Date.now();
      } else {
        // Already at destination, just complete
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }
    }
  }, [isStoryReady, pendingDestination, isAnimating, visualPosition, playerPosition, onAnimationComplete]);
  
  // Helper function to interpolate position along a route
  const interpolateAlongRoute = useCallback((route: AxialCoord[] | undefined, progress: number, fallbackStart: AxialCoord, fallbackEnd: AxialCoord): AxialCoord => {
    // If no route or route is too short, use straight line
    if (!route || route.length < 2) {
      return {
        q: fallbackStart.q + (fallbackEnd.q - fallbackStart.q) * progress,
        r: fallbackStart.r + (fallbackEnd.r - fallbackStart.r) * progress
      };
    }
    
    // Calculate which segment we're on
    const totalSegments = route.length - 1;
    const segmentProgress = progress * totalSegments;
    const currentSegmentIndex = Math.floor(segmentProgress);
    const segmentLocalProgress = segmentProgress - currentSegmentIndex;
    
    // Clamp to valid segment
    const segmentIndex = Math.min(currentSegmentIndex, totalSegments - 1);
    const segmentStart = route[segmentIndex];
    const segmentEnd = route[segmentIndex + 1];
    
    // Interpolate within the current segment
    return {
      q: segmentStart.q + (segmentEnd.q - segmentStart.q) * segmentLocalProgress,
      r: segmentStart.r + (segmentEnd.r - segmentStart.r) * segmentLocalProgress
    };
  }, []);

  // Animation loop
  useEffect(() => {
    if (!isAnimating || !pendingDestination || !visualPosition) return;
    
    const startPosition = { ...visualPosition }; // Capture start position
    const endPosition = { ...pendingDestination };
    
    const animate = () => {
      if (!animationStartTimeRef.current) return;
      
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // Ease-in-out function
      const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const easedProgress = easeInOut(progress);
      
      // Interpolate position along route (or straight line if no route)
      const newPosition = interpolateAlongRoute(plannedRoute, easedProgress, startPosition, endPosition);
      
      setVisualPosition(newPosition);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - ensure visual position matches destination
        setVisualPosition(endPosition);
        setIsAnimating(false);
        animationStartTimeRef.current = null;
        
        // Use setTimeout to batch the callback and ensure render happens first
        setTimeout(() => {
          if (onAnimationComplete) {
            onAnimationComplete();
          }
        }, 0);
      }
    };
    
    const frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isAnimating, pendingDestination, onAnimationComplete, plannedRoute, interpolateAlongRoute]);

  // Calculate hexes to display (center + 2 rings = 19 hexes)
  const getVisibleHexes = useCallback(() => {
    const firstRing = hexNeighbors(center);
    const secondRing: AxialCoord[] = [];
    
    // Get neighbors of neighbors (excluding duplicates)
    const seen = new Set<string>([`${center.q},${center.r}`]);
    firstRing.forEach(coord => seen.add(`${coord.q},${coord.r}`));
    
    firstRing.forEach(firstRingHex => {
      hexNeighbors(firstRingHex).forEach(neighbor => {
        const key = `${neighbor.q},${neighbor.r}`;
        if (!seen.has(key)) {
          secondRing.push(neighbor);
          seen.add(key);
        }
      });
    });
    
    return [center, ...firstRing, ...secondRing];
  }, [center]);

  // Sort hexes by depth for proper rendering (back-to-front)
  const getSortedHexes = useCallback(() => {
    const hexes = getVisibleHexes();
    return hexes.sort((a, b) => {
      const depthA = hexDepthIsometric(a, center);
      const depthB = hexDepthIsometric(b, center);
      return depthB - depthA; // Higher depth (further back) renders first
    });
  }, [center, getVisibleHexes]);

  // Render the mini map
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.warn('⚠️ MiniIsometricMap: No canvas ref');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.warn('⚠️ MiniIsometricMap: No 2d context');
      return;
    }
    
    // Ensure canvas has valid dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('⚠️ MiniIsometricMap: Canvas has zero dimensions');
      return;
    }

    // Clear and fill background
    clearCanvas(ctx, canvas.width, canvas.height);
    fillBackground(ctx, canvas.width, canvas.height, '#2a2a2a');

    // Save context for transformations
    ctx.save();

    // Center the view on the canvas and apply pan offset
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.translate(centerX + panOffset.x, centerY + panOffset.y);

    // Ensure map exists
    if (!map) {
      console.warn('⚠️ MiniIsometricMap: No map data');
      return;
    }

    // Get sorted hexes for rendering
    const sortedHexes = getSortedHexes();

    // Render each hex in depth order
    for (const hex of sortedHexes) {
      const hexData = map.hexes.get(`${hex.q},${hex.r}`);
      if (!hexData) continue;

      const isExplored = exploredHexes.has(`${hex.q},${hex.r}`);
      
      // Create a relative hex coordinate (offset from center)
      const relativeHex: AxialCoord = {
        q: hex.q - center.q,
        r: hex.r - center.r
      };
      
      // Draw hex with actual terrain
      drawHexIsometricSync(ctx, relativeHex, hexData.terrain, HEX_SIZE, true);
      
      // Add fog of war overlay for unexplored hexes
      if (!isExplored && !(hex.q === center.q && hex.r === center.r)) {
        const corners = require('../utils/hexMath').hexCornersIsometric(relativeHex, HEX_SIZE);
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++) {
          ctx.lineTo(corners[i].x, corners[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.fill();
      }

      // Show movement range (adjacent to player if stamina available and can move)
      if (playerStamina > 0 && canMove) {
        const isAdjacentToPlayer = hexNeighbors(playerPosition).some(
          neighbor => neighbor.q === hex.q && neighbor.r === hex.r
        );
        // Highlight adjacent hexes as moveable (both explored and unexplored)
        if (isAdjacentToPlayer) {
          drawHighlightedHexIsometric(ctx, relativeHex, HEX_SIZE, '#88ff88', 0.3);
        }
      }

      // Highlight hovered hex
      if (hoveredHex && hex.q === hoveredHex.q && hex.r === hoveredHex.r) {
        drawHighlightedHexIsometric(ctx, relativeHex, HEX_SIZE, '#ffff00', 0.4);
      }

      // Highlight player position
      if (hex.q === playerPosition.q && hex.r === playerPosition.r) {
        drawHighlightedHexIsometric(ctx, relativeHex, HEX_SIZE, '#00ff00', 0.4);
      }
    }

    // Draw planned route (if exists)
    if (plannedRoute && plannedRoute.length > 1) {
      // Convert route to relative coordinates
      const relativeRoute = plannedRoute.map(hex => ({
        q: hex.q - center.q,
        r: hex.r - center.r
      }));
      drawRouteOverlay(ctx, relativeRoute, HEX_SIZE, true);
      drawRouteLine(ctx, relativeRoute, HEX_SIZE, true);
    }

    // Draw destination hex highlight (if pending - show during travel)
    if (pendingDestination) {
      const relativeDestHex: AxialCoord = {
        q: pendingDestination.q - center.q,
        r: pendingDestination.r - center.r
      };
      drawHighlightedHexIsometric(ctx, relativeDestHex, HEX_SIZE, '#ffff00', 0.3);
    }
    
    // Draw player marker at visual position (after all hexes so it's on top)
    const markerPosition = visualPosition || playerPosition;
    if (playerImageRef.current) {
      const relativePlayerHex: AxialCoord = {
        q: markerPosition.q - center.q,
        r: markerPosition.r - center.r
      };
      
      // Get hex corners for proper hexagonal positioning
      const corners = hexCornersIsometric(relativePlayerHex, HEX_SIZE);
      
      // TODO: Replace with actual multiplayer data when multiplayer is implemented
      // For now, create mock data for testing segmented markers
      // This demonstrates 1-4 players based on hex coordinates (for testing purposes)
      const mockPlayerCount = 1; // Change this to 1, 2, or 3 to test different segments
      const playersOnHex: PlayerMarkerType[] = Array.from({ length: mockPlayerCount }, (_, i) => ({
        playerId: `player-${i}`,
        color: PLAYER_COLORS[i],
      }));
      
      // Draw segmented player marker (supports 1-4 players)
      drawSegmentedPlayerMarker(ctx, corners, playersOnHex, playerImageRef.current);
    }

    ctx.restore();
  }, [map, center, playerPosition, exploredHexes, hoveredHex, playerStamina, canMove, getSortedHexes, HEX_SIZE, playerImageLoaded, visualPosition, pendingDestination, plannedRoute, panOffset]);

  // Handle canvas resize - match container dimensions exactly
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set initial size immediately if container has dimensions
    const rect = container.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      // Use actual container dimensions to prevent gaps
      setCanvasSize({ width: rect.width, height: rect.height });
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Use actual dimensions to fill container exactly
        if (width > 0 && height > 0) {
          setCanvasSize({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Re-render when dependencies change, but only if canvas has valid dimensions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Wait for React to apply canvas dimensions, then render
    // Use double RAF to ensure dimensions are applied after React's render cycle
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Only render if canvas has valid dimensions
        if (canvas.width > 0 && canvas.height > 0) {
          render();
          hasRenderedRef.current = true;
        } else if (!hasRenderedRef.current) {
          // If we haven't rendered yet and dimensions are still 0, try again after a short delay
          // This handles the case where the container hasn't been measured yet
          setTimeout(() => {
            const canvas = canvasRef.current;
            if (canvas && canvas.width > 0 && canvas.height > 0) {
              render();
              hasRenderedRef.current = true;
            }
          }, 50);
        }
      });
    });
  }, [render, canvasSize]);

  // Handle mouse move for hover effects
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Scale mouse coordinates to canvas buffer coordinates
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = mouseX * scaleX;
    const y = mouseY * scaleY;

    // Convert to canvas coordinates (account for centering and pan offset)
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const canvasX = x - centerX - panOffset.x;
    const canvasY = y - centerY - panOffset.y;

    // Convert to relative hex coordinates
    const relativeHex = pixelToHexIsometric({ x: canvasX, y: canvasY }, HEX_SIZE);
    
    // Convert back to absolute hex coordinates
    const absoluteHex: AxialCoord = {
      q: relativeHex.q + center.q,
      r: relativeHex.r + center.r
    };

    // Check if this hex is in our visible set
    const visibleHexes = getVisibleHexes();
    const isVisible = visibleHexes.some(h => h.q === absoluteHex.q && h.r === absoluteHex.r);

    if (isVisible) {
      // Only log when hover changes
      if (!hoveredHex || hoveredHex.q !== absoluteHex.q || hoveredHex.r !== absoluteHex.r) {
        console.log('🎯 Hovering hex:', absoluteHex, 'relative:', relativeHex);
      }
      setHoveredHex(absoluteHex);
    } else {
      if (hoveredHex) {
        console.log('❌ Mouse left hex area');
      }
      setHoveredHex(null);
    }
  }, [center, getVisibleHexes, HEX_SIZE, hoveredHex, panOffset]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredHex(null);
  }, []);

  // Handle click
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Recalculate hex position at click time (don't rely on hover state)
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Scale mouse coordinates to canvas buffer coordinates
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = mouseX * scaleX;
    const y = mouseY * scaleY;

    // Convert to canvas coordinates (account for centering and pan offset)
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const canvasX = x - centerX - panOffset.x;
    const canvasY = y - centerY - panOffset.y;

    // Convert to relative hex coordinates
    const relativeHex = pixelToHexIsometric({ x: canvasX, y: canvasY }, HEX_SIZE);
    
    // Convert back to absolute hex coordinates
    const clickedHex: AxialCoord = {
      q: relativeHex.q + center.q,
      r: relativeHex.r + center.r
    };
    
    console.log('🖱️ Mini map clicked at:', clickedHex);

    // Check if this hex is in our visible set
    const visibleHexes = getVisibleHexes();
    const isVisible = visibleHexes.some(h => h.q === clickedHex.q && h.r === clickedHex.r);
    
    if (!isVisible) {
      console.log('❌ Clicked hex not in visible set');
      return;
    }

    // Check if hex is adjacent to player (movement range)
    const neighbors = hexNeighbors(playerPosition);
    const isAdjacentToPlayer = neighbors.some(
      neighbor => neighbor.q === clickedHex.q && neighbor.r === clickedHex.r
    );
    
    console.log('👣 Click on mini map:', {
      playerPos: playerPosition,
      clickedHex,
      isAdjacent: isAdjacentToPlayer,
      isExplored: exploredHexes.has(`${clickedHex.q},${clickedHex.r}`),
      stamina: playerStamina,
      canMove,
    });

    // Allow movement to any adjacent hex (explored or unexplored) if can move
    // Note: Stamina check moved to App.tsx handleMiniMapHexClick to allow daily reset logic to run
    if (isAdjacentToPlayer && canMove) {
      console.log('✅ Passing click to handler:', clickedHex);
      onHexClick(clickedHex);
    } else if (!isAdjacentToPlayer && isVisible) {
      // Clicked on a visible but non-adjacent hex
      setShowTravelModal(true);
      console.log('ℹ️ Non-adjacent hex clicked, directing to map view');
    } else {
      console.log('❌ Cannot move:', { isAdjacentToPlayer, canMove });
    }
  }, [center, exploredHexes, playerPosition, playerStamina, canMove, onHexClick, getVisibleHexes, HEX_SIZE, panOffset]);

  // Register touch event handlers with { passive: false } to allow preventDefault()
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        lastPanOffsetRef.current = panOffset;
        setIsDragging(false); // Reset dragging state
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1 && touchStartRef.current) {
        e.preventDefault(); // Prevent page scrolling
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchStartRef.current.x;
        const deltaY = touch.clientY - touchStartRef.current.y;
        
        // Consider it a drag if moved more than 5 pixels
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          setIsDragging(true);
        }
        
        // Update pan offset
        setPanOffset({
          x: lastPanOffsetRef.current.x + deltaX,
          y: lastPanOffsetRef.current.y + deltaY
        });
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // If it was a tap (not a drag), treat it as a click
      if (!isDragging && touchStartRef.current && e.changedTouches.length === 1) {
        // Prevent the subsequent click event from firing
        e.preventDefault();
        
        const touch = e.changedTouches[0];
        const rect = canvas.getBoundingClientRect();
        const touchX = touch.clientX - rect.left;
        const touchY = touch.clientY - rect.top;

        // Scale touch coordinates to canvas buffer coordinates
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = touchX * scaleX;
        const y = touchY * scaleY;

        // Convert to canvas coordinates (account for centering and pan offset)
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const canvasX = x - centerX - panOffset.x;
        const canvasY = y - centerY - panOffset.y;

        // Convert to relative hex coordinates
        const relativeHex = pixelToHexIsometric({ x: canvasX, y: canvasY }, HEX_SIZE);
        
        // Convert back to absolute hex coordinates
        const tappedHex: AxialCoord = {
          q: relativeHex.q + center.q,
          r: relativeHex.r + center.r
        };
        
        console.log('👆 Mini map tapped at:', tappedHex);

        // Check if this hex is in our visible set
        const visibleHexes = getVisibleHexes();
        const isVisible = visibleHexes.some(h => h.q === tappedHex.q && h.r === tappedHex.r);
        
        if (isVisible) {
          // Check adjacency for movement
          const neighbors = hexNeighbors(playerPosition);
          const isAdjacentToPlayer = neighbors.some(
            neighbor => neighbor.q === tappedHex.q && neighbor.r === tappedHex.r
          );
          
          if (isAdjacentToPlayer && canMove) {
            onHexClick(tappedHex);
          } else if (!isAdjacentToPlayer && isVisible) {
            setShowTravelModal(true);
          }
        }
      }
      
      touchStartRef.current = null;
      setIsDragging(false);
    };

    // Register with { passive: false } to allow preventDefault()
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [panOffset, isDragging, center, onHexClick, getVisibleHexes, HEX_SIZE, playerPosition, canMove]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative', 
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '2px solid #4a4a4a',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        boxSizing: 'border-box',
      }}
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        data-no-sync="true"
        style={{
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          cursor: hoveredHex ? 'pointer' : 'default',
          display: 'block',
          touchAction: 'none', // Prevent browser touch gestures
        }}
      />
      
      {/* Forage/Hunt Button */}
      {FEATURES.MANUAL_STAMINA_RECOVERY && onForageClick && (
        <button
          onClick={onForageClick}
          disabled={!canForage}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            padding: '8px 12px',
            backgroundColor: canForage ? 'rgba(34, 139, 34, 0.85)' : 'rgba(100, 100, 100, 0.5)',
            color: canForage ? '#f5e6d3' : '#999',
            border: `2px solid ${canForage ? '#228b22' : '#666'}`,
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            cursor: canForage ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
          onMouseEnter={(e) => {
            if (canForage) {
              e.currentTarget.style.backgroundColor = 'rgba(34, 139, 34, 1)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (canForage) {
              e.currentTarget.style.backgroundColor = 'rgba(34, 139, 34, 0.85)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
          title={canForage ? "Forage for rations (1 hour)" : "Cannot forage (rations full or invalid terrain)"}
        >
          Forage/Hunt
        </button>
      )}
      
      {/* Rest/Heal Button */}
      {FEATURES.MANUAL_STAMINA_RECOVERY && onRestHealClick && (
        <button
          onClick={onRestHealClick}
          disabled={isInCombat || !canMove}
          style={{
            position: 'absolute',
            top: '10px',
            left: '130px', // Positioned to the right of Forage/Hunt button
            padding: '8px 12px',
            backgroundColor: (isInCombat || !canMove) ? 'rgba(100, 100, 100, 0.5)' : 'rgba(34, 139, 34, 0.85)',
            color: (isInCombat || !canMove) ? '#999' : '#f5e6d3',
            border: `2px solid ${(isInCombat || !canMove) ? '#666' : '#228b22'}`,
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            cursor: (isInCombat || !canMove) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
          onMouseEnter={(e) => {
            if (!isInCombat && canMove) {
              e.currentTarget.style.backgroundColor = 'rgba(34, 139, 34, 1)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isInCombat && canMove) {
              e.currentTarget.style.backgroundColor = 'rgba(34, 139, 34, 0.85)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
          title={(isInCombat || !canMove) ? "Cannot rest/heal (in combat or busy)" : "Rest/Heal"}
        >
          Rest/Heal
        </button>
      )}
      
      {/* Travel Distance Modal */}
      {showTravelModal && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowTravelModal(false)}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              color: '#e0e0e0',
              padding: '32px',
              borderRadius: '8px',
              minWidth: '300px',
              maxWidth: '500px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              textAlign: 'center',
              fontFamily: "'Lora', serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ 
              fontSize: '18px', 
              lineHeight: '1.6',
              margin: '0 0 24px 0',
            }}>
              If you want to travel a significant distance, you can do so on the map view.
            </p>
            <button
              onClick={() => setShowTravelModal(false)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#4a7c59',
                color: '#f5e6d3',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontFamily: "'Cinzel', serif",
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5a8c69';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#4a7c59';
              }}
            >
              OK
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Export memoized version to prevent unnecessary re-renders
export const MiniIsometricMap = memo(MiniIsometricMapComponent);

